import { createStore } from 'zustand/vanilla';
import type { FreeLimitType, PlanUsageModel } from './types';

export interface LimitModalRequest {
  readonly type: FreeLimitType;
  readonly resetDays?: number | null;
  readonly usageCount?: number | null;
  readonly usageLimit?: number | null;
  readonly onNotNow?: () => void;
  readonly onUpgrade?: () => void;
}

interface PlanUsageStoreState {
  usage: PlanUsageModel | null;
  modalRequest: LimitModalRequest | null;
  setUsage: (usage: PlanUsageModel) => void;
  openModal: (request: LimitModalRequest) => void;
  closeModal: () => void;
}

export const planUsageStore = createStore<PlanUsageStoreState>((set) => ({
  usage: null,
  modalRequest: null,
  setUsage: (usage) => set({ usage }),
  openModal: (request) => set({ modalRequest: request }),
  closeModal: () => set({ modalRequest: null }),
}));

// ─── Pro-only feature list ────────────────────────────────────────────────────

const PRO_ONLY: ReadonlySet<FreeLimitType> = new Set<FreeLimitType>([
  'scanWithMultiCurrency',
  'groupActivitySearchOrder',
  'exportFilterLimit',
  'advancedCharts',
  'reportIssue',
  'webAccess',
  'recurringExpenses',
  'currencyConversion',
  'groupCurrencyConversion',
  'olderAttachmentLimit',
  'attachmentSizeLimit',
]);

// ─── Private limit-check logic ────────────────────────────────────────────────

function isLimitReached(usage: PlanUsageModel | null, type: FreeLimitType): boolean {
  if (!usage) {
    // No data loaded yet — block Pro-only features, fail open for counter-based ones.
    return PRO_ONLY.has(type);
  }
  switch (type) {
    case 'groupLimit':
      return usage.groups?.isLimitReached ?? false;
    case 'walletLimit':
      return usage.wallets?.isLimitReached ?? false;
    case 'scanLimit':
      return usage.receiptScans?.isLimitReached ?? false;
    case 'attachmentWeeklyLimit':
      return usage.attachments?.isWeeklyLimitReached ?? false;
    case 'exportMonthlyLimit':
      return usage.exports?.isMonthlyLimitReached ?? false;
    case 'deviceLimit':
      return usage.devices?.isLimitReached ?? false;
    case 'personalReminderLimit':
      return usage.reminders?.combinedReminders?.isLimitReached ?? false;
    default:
      // Pro-only and any unknown type: blocked for free users.
      return PRO_ONLY.has(type);
  }
}

function getResetDays(usage: PlanUsageModel | null, type: FreeLimitType): number | null {
  if (!usage) return null;
  switch (type) {
    case 'attachmentWeeklyLimit':
      return usage.attachments?.daysUntilReset ?? null;
    case 'exportMonthlyLimit':
      return usage.exports?.daysUntilReset ?? null;
    case 'personalReminderLimit':
      return usage.reminders?.combinedReminders?.daysUntilReset ?? null;
    default:
      return null;
  }
}

function getUsageInfo(
  usage: PlanUsageModel | null,
  type: FreeLimitType,
): { count?: number; limit?: number } {
  if (!usage) return {};
  switch (type) {
    case 'exportMonthlyLimit':
      return { count: usage.exports?.monthUsed, limit: usage.exports?.monthlyLimit };
    case 'attachmentWeeklyLimit':
      return { count: usage.attachments?.weekUsed, limit: usage.attachments?.weeklyLimit };
    default:
      return {};
  }
}

// ─── Public manager ───────────────────────────────────────────────────────────

export interface CheckOptions {
  onNotNow?: () => void;
  onUpgrade?: () => void;
}

export const planUsageManager = {
  setUsage(usage: PlanUsageModel): void {
    planUsageStore.getState().setUsage(usage);
  },

  isPro(): boolean {
    return planUsageStore.getState().usage?.isPro ?? false;
  },

  /**
   * Check whether `type` is allowed. If not, opens the limit-reached modal and returns `false`.
   * Returns `true` when the action should proceed (Pro user, or limit not yet reached).
   */
  check(type: FreeLimitType, opts?: CheckOptions): boolean {
    const { usage } = planUsageStore.getState();
    if (usage?.isPro) return true;
    if (!isLimitReached(usage, type)) return true;

    const { count, limit } = getUsageInfo(usage, type);
    planUsageStore.getState().openModal({
      type,
      resetDays: getResetDays(usage, type),
      usageCount: count,
      usageLimit: limit,
      onNotNow: opts?.onNotNow,
      onUpgrade: opts?.onUpgrade,
    });
    return false;
  },

  /** Silent check — returns boolean without opening any UI. For hiding/showing elements. */
  isAllowed(type: FreeLimitType): boolean {
    const { usage } = planUsageStore.getState();
    if (usage?.isPro) return true;
    return !isLimitReached(usage, type);
  },
};
